import type { ReactNode } from 'react';

import { usePositionedPopup } from '../../hooks/use-positioned-popup/use-positioned-popup';

import type { ControlledProps, DropdownItems } from './dropdown-shared';
import { type DropdownSize, Dropdown } from './dropdown-shared';
import { useDropdown } from './utilities';

export type DropdownSearchProps<Value = string> = {
  label: ReactNode;
  placeholder?: string;
  size?: DropdownSize;
  disabled?: boolean;
  expand?: boolean;
  items: DropdownItems<Value>;
  noResultsLabel: ReactNode;
} & ControlledProps<Value>;

export const DropdownSearch = <Value,>({
  disabled = false,
  items,
  label,
  placeholder,
  size = 'medium',
  onChange,
  value = '',
  expand = false,
  noResultsLabel,
}: DropdownSearchProps<Value>) => {
  'use no memo';
  const {
    menuItems,
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useDropdown({
    items,
    onChange,
    value,
  });

  const { setFloating, setReference, floatingStyles, constrainStyles } =
    usePositionedPopup({
      open: isOpen,
    });

  return (
    <Dropdown
      expand={expand}
      label={label}
      disabled={disabled}
      labelProps={getLabelProps()}
    >
      <Dropdown.SearchInput
        size={size}
        disabled={disabled}
        isOpen={isOpen}
        name="dropdown-with-search"
        toggleButtonProps={getToggleButtonProps()}
        getInputProps={getInputProps}
        ref={setReference}
        placeholder={placeholder}
        selectedItem={selectedItem}
      />

      <Dropdown.Menu
        menuProps={getMenuProps()}
        floatingStyles={floatingStyles}
        setFloating={setFloating}
        disabled={disabled}
        isOpen={isOpen}
        constrainStyles={constrainStyles}
      >
        {menuItems.length > 0 ? (
          menuItems.map((item, index) => (
            <Dropdown.MenuItem
              highlightedIndex={highlightedIndex}
              item={item}
              index={index}
              size={size}
              key={`${item.value || index}`}
              isSelected={value === item.value}
              {...getItemProps({ item, index })}
            />
          ))
        ) : (
          <Dropdown.NoResults>{noResultsLabel}</Dropdown.NoResults>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};
